export interface SupportStep {
  sessionToken: string;
  version: number;
  expiresAt: string;
  message: string;
  isComplete: boolean;
  options: { id: string; label: string }[];
}

export class SupportError extends Error {
  constructor(public status: number, public retryAfter = 0) { super('Support request failed'); }
}

// Use plain fetch, not the app's authenticated client: this service needs only its own session token.
export async function supportRequest(baseUrl: string, language: 'ar' | 'en', signal: AbortSignal,
  step?: SupportStep, optionId?: string): Promise<SupportStep> {
  const url = new URL(baseUrl);
  const localHttp = url.protocol === 'http:' && ['localhost', '127.0.0.1', '[::1]'].includes(url.hostname);
  if (url.username || url.password || url.search || url.hash || url.pathname !== '/' ||
      (url.protocol !== 'https:' && !(process.env.NODE_ENV === 'development' && localHttp))) {
    throw new SupportError(0);
  }
  const response = await fetch(`${url.origin}/api/support/${step ? 'select' : 'start'}`, {
    method: 'POST', signal, credentials: 'omit', cache: 'no-store', redirect: 'error',
    headers: { 'Content-Type': 'application/json', ...(step ? { Authorization: `Bearer ${step.sessionToken}` } : {}) },
    body: JSON.stringify(step ? { optionId, version: step.version } : { language }),
  });
  if (!response.ok) {
    const retry = response.headers.get('Retry-After');
    const seconds = retry && /^\d+$/.test(retry) ? Number(retry) : 60;
    throw new SupportError(response.status, Math.min(300, Math.max(1, seconds)));
  }
  const value = await response.json();
  // An unexpected response should show a recovery message, not break the rest of the website.
  if (!value || !/^[A-F0-9]{64}$/.test(value.sessionToken) || !Number.isInteger(value.version) || value.version < 0 ||
      typeof value.message !== 'string' || typeof value.isComplete !== 'boolean' ||
      typeof value.expiresAt !== 'string' || !Number.isFinite(Date.parse(value.expiresAt)) ||
      !Array.isArray(value.options) || value.options.length > 20 ||
      !value.options.every((o: any) => o && typeof o.id === 'string' && typeof o.label === 'string')) {
    throw new SupportError(0);
  }
  return value;
}
